// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
The price on the listing is rarely the price at your door. Buying jewelry across a border can add import duty, sales tax or VAT, and a carrier's handling fee on top, and in most countries the buyer pays all three. Knowing that before you commit is the difference between a good purchase and an unpleasant surprise.

## The three separate charges

People say "customs" and mean one of three things. They are charged differently and it helps to keep them apart.

**Import duty.** A tax on the goods themselves, set as a percentage of the value, varying by what the item is and where it came from. Jewelry duty rates are usually modest.

**Sales tax or VAT.** Charged at your country's normal rate on the value of the goods, often including the shipping cost and sometimes including the duty. This is almost always the largest of the three.

**Carrier handling fee.** What the courier charges for clearing the parcel and advancing the tax on your behalf. A flat fee, and it stings most on cheap items.

A small parcel can therefore attract more in fees than the item is worth, which is why very low value cross-border purchases often make no sense.

## Who pays: DDP and DDU

Two abbreviations decide everything.

**DDU or DAP, delivered duty unpaid.** The seller ships, the parcel reaches your country, and the carrier contacts you for the tax and their fee before delivering. This is the default for most international sales.

**DDP, delivered duty paid.** The seller has pre-paid the duty and tax, and the parcel arrives with nothing further to pay. More expensive up front, no surprises later.

If a listing does not say, assume DDU and assume you will be billed. Ask the seller directly, because it changes the real price.

## Second hand and antique: the useful exceptions

Two things sometimes work in a buyer's favour, and both depend on where you live.

**Antiques over 100 years old** attract a reduced or zero rate of import duty in a number of jurisdictions, on the reasoning that they are cultural goods rather than new manufacture. Where this applies, the seller usually has to declare the item under the right customs code and, in some cases, evidence its age. It rarely removes sales tax or VAT, only the duty.

**Second hand goods** are not automatically exempt from anything. A pre-owned ring crossing a border is treated as an import like any other.

Both of these vary by country and change over time. Treat them as worth asking about, not as a rule you can rely on.

## What the declared value should say

The declared value must be what you actually paid. That is not a grey area.

Under-declaring to reduce tax is fraud. It also voids the shipping insurance, because a carrier will only compensate up to the declared value. A seller who offers to write a low value on the form is offering to make your parcel uninsured in exchange for a small saving, and if it goes missing you will get back what was declared, not what you paid.

Marking a purchase as a gift does not change this. It is a false declaration.

## Returns, and the money you do not get back

This is the part that catches people out, and it is worth understanding before you buy something you are unsure about.

If you import a piece, pay duty and tax, and then return it, that money does not automatically come back with the parcel. Reclaiming import charges on returned goods is possible in many countries but it is a separate process, it takes paperwork, and the carrier's handling fee is usually gone for good.

Which means: **a cross-border return can cost you real money even when the refund is full.** Ask more questions before buying from abroad than you would at home. Our [guide to buying an antique engagement ring](/blog/how-to-buy-an-antique-engagement-ring) lists the questions that prevent most returns.

If you do return an item internationally, mark the customs paperwork clearly as **returned goods**, or the seller's country may charge import tax to receive its own item back, and that cost will usually land on you.

## What sellers should do

If you are on the selling side:

- **State your shipping terms.** Say where you ship from, and whether duties are pre-paid or the buyer's responsibility.
- **Never mis-declare.** It voids insurance and it is fraud.
- **Use tracked, insured shipping** and keep the proof of posting. Our [guide to selling safely](/blog/how-to-sell-jewelry-safely-online) covers the shipping traps in detail.
- **Put it in the listing, not in a message.** A buyer who reads about duties after the parcel arrives feels misled, even when you did nothing wrong.

## Practical questions to ask before you buy from abroad

1. Where does the piece ship from?
2. Is it sent duty paid, or will I be billed on arrival?
3. What value will be declared, and will it match what I paid?
4. Is it insured for the full amount?
5. Is the piece over 100 years old, and will that be stated on the customs paperwork?
6. If I return it, who handles the import charges?

## How it works on Nextmine

Sellers set their own shipping and state it on the listing, and every sale must ship insured and tracked. Prices are in US dollars, so your bank sets the exchange rate and may add its own fee on top.

Import duties and taxes charged by your own country are the buyer's responsibility, and that is stated in our [terms](/terms-of-service) rather than left as a surprise. If a seller does not ship to your country at a listed price, you can request a shipping quote from the listing and get an exact figure for insured, tracked delivery before you commit.

The payment itself is held until the piece has arrived and you have confirmed it. Read [how payments work](/how-payments-work), or [see what is listed right now](/s).
`;

export default body;
